import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Award,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Circle,
  Clock,
  FileText,
  Loader2,
  Sparkles,
  XCircle,
  Zap,
  RefreshCw,
  AlertTriangle,
  BookOpen,
  Eye,
  Image,
  Tag,
  Users,
  TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import { certificationService } from '@/services/certification.service';
import { CardSkeleton, SkeletonLoader } from '@/components/ui/SkeletonLoader';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATION_CATALOG_PERMISSION } from '@/constants/constant-permissions';

interface GenerationStage {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
}

export default function CertificationGenerationStatus() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isGenerating, setIsGenerating] = useState(true);
  const [generationStatus, setGenerationStatus] = useState<any>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [isRetrying, setIsRetrying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stageElapsedTimes, setStageElapsedTimes] = useState<Record<string, number>>({});

  const canViewDetails = useHasPermission([CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_WRITE]);
  const canGoToCertifications = useHasPermission([
    CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_READ,
    CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_WRITE,
  ]);

  // Track user-manually opened stages to prevent auto-collapse
  const userOpenedStagesRef = useRef<Set<string>>(new Set());

  // Generation stages configuration (NEW 3-stage model) - Memoized to prevent infinite loops
  const stages: GenerationStage[] = useMemo(() => [
    {
      key: 'stage_1_metadata',
      label: 'Stage 1: Metadata Generation',
      description: 'AI generating certification name, slug, description, and tags',
      icon: Sparkles,
      color: 'from-purple-500 to-indigo-600'
    },
    {
      key: 'stage_2_images',
      label: 'Stage 2: Image Generation',
      description: 'AI generating professional banner and thumbnail images',
      icon: FileText,
      color: 'from-pink-500 to-rose-600'
    },
    {
      key: 'stage_3_questions',
      label: 'Stage 3: Question Generation',
      description: 'AI extracting content and generating exam questions',
      icon: BookOpen,
      color: 'from-green-500 to-emerald-600'
    }
  ], []);

  // Calculate overall elapsed time in real-time
  useEffect(() => {
    if (!generationStatus?.metrics?.started_at) return;

    const updateElapsedTime = () => {
      const startTime = new Date(generationStatus.metrics.started_at).getTime();
      const now = Date.now();
      const elapsed = now - startTime;
      setElapsedTime(elapsed);
    };

    // Update immediately
    updateElapsedTime();

    // Update every second if still generating
    if (isGenerating) {
      const interval = setInterval(updateElapsedTime, 1000);
      return () => clearInterval(interval);
    }
  }, [generationStatus?.metrics?.started_at, isGenerating]);

  // Calculate elapsed time for each stage in real-time
  useEffect(() => {
    if (!generationStatus?.stages) return;

    const updateStageElapsedTimes = () => {
      const newTimes: Record<string, number> = {};

      stages.forEach((stage) => {
        const stageData = generationStatus.stages[stage.key];
        if (!stageData) return;

        const status = stageData.status?.toLowerCase();

        // If stage is processing, calculate elapsed time
        if (status === 'processing' || status === 'in_progress') {
          const startedAt = stageData.started_at;
          if (startedAt) {
            const startTime = new Date(startedAt).getTime();
            const now = Date.now();
            newTimes[stage.key] = now - startTime;
          }
        }
        // If stage is completed or failed, use the final duration
        else if ((status === 'completed' || status === 'failed') && stageData.duration_ms) {
          newTimes[stage.key] = stageData.duration_ms;
        }
      });

      setStageElapsedTimes(newTimes);
    };

    // Update immediately
    updateStageElapsedTimes();

    // Update every second if still generating
    if (isGenerating) {
      const interval = setInterval(updateStageElapsedTimes, 1000);
      return () => clearInterval(interval);
    }
  }, [generationStatus?.stages, isGenerating, stages]);

  // Toggle stage expansion
  const toggleStageExpansion = (stageKey: string) => {
    setExpandedStages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stageKey)) {
        newSet.delete(stageKey);
        // Remove from user-opened tracking when manually closed
        userOpenedStagesRef.current.delete(stageKey);
      } else {
        newSet.add(stageKey);
        // Mark as user-opened to prevent auto-collapse
        userOpenedStagesRef.current.add(stageKey);
      }
      return newSet;
    });
  };

  // Load generation status
  const loadGenerationStatus = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoadingStatus(true);
      const status = await certificationService.getCertificationGenerationStatus(id);
      setGenerationStatus(status);

      // Check if still generating
      const overallStatus = status.overall_status?.toLowerCase();
      const isStillGenerating = overallStatus === 'pending' || overallStatus === 'in_progress' || overallStatus === 'processing';
      setIsGenerating(isStillGenerating);

      // Auto-expand active or failed stages (but respect user-opened stages)
      setExpandedStages(prev => {
        const newSet = new Set(prev);
        stages.forEach(stage => {
          const stageStatus = status.stages?.[stage.key]?.status?.toLowerCase();
          const isUserOpened = userOpenedStagesRef.current.has(stage.key);

          if (stageStatus === 'processing' || stageStatus === 'in_progress' || stageStatus === 'failed') {
            newSet.add(stage.key);
          } else if (stageStatus === 'completed' && !isUserOpened) {
            // Only auto-collapse if user didn't manually open it
            newSet.delete(stage.key);
          }
        });
        return newSet;
      });

    } catch (error: any) {
      console.error('Error loading generation status:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load generation status');
      }
    } finally {
      setIsLoadingStatus(false);
    }
  }, [id]);

  // Main useEffect
  useEffect(() => {
    if (!id) {
      toast.error('No certification ID found');
      navigate('/certifications');
      return;
    }

    loadGenerationStatus();
  }, [id, navigate, loadGenerationStatus]);

  // Timer effect for polling every 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isGenerating) {
      timer = setInterval(() => {
        loadGenerationStatus();
      }, 5000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isGenerating, loadGenerationStatus]);

  const getStageStatus = (stageName: string) => {
    if (!generationStatus?.stages) return { status: 'pending', icon: Circle, color: 'text-gray-400' };

    const stageData = generationStatus.stages[stageName];
    const status = stageData?.status?.toLowerCase() || 'pending';

    switch (status) {
      case 'completed':
        return { status, icon: CheckCircle, color: 'text-green-600' };
      case 'processing':
      case 'in_progress':
        return { status: 'processing', icon: Loader2, color: 'text-blue-600', animate: true };
      case 'failed':
        return { status, icon: XCircle, color: 'text-red-600' };
      default:
        return { status: 'pending', icon: Circle, color: 'text-gray-400' };
    }
  };

  const handleRetry = async () => {
    if (!id) return;

    setIsRetrying(true);
    try {
      await certificationService.retryExamGeneration(id);
      toast.success('Exam generation restarted successfully!');
      setIsGenerating(true);
      setTimeout(() => {
        loadGenerationStatus();
      }, 2000);
    } catch (error: any) {
      console.error('Retry error:', error);
      toast.error(error.response?.data?.message || 'Failed to retry exam generation');
    } finally {
      setIsRetrying(false);
    }
  };

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Missing Certification ID</h2>
          <p className="text-gray-600 mb-4">No certification ID provided.</p>
          <button
            onClick={() => navigate('/certifications')}
            className="px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700"
          >
            Go to Certifications
          </button>
        </div>
      </div>
    );
  }

  const renderTimelineItem = (stage: GenerationStage, index: number) => {
    const stageStatus = getStageStatus(stage.key);
    const IconComponent = stageStatus.icon;
    const isCompleted = stageStatus.status === 'completed';
    const stageData = generationStatus?.stages?.[stage.key];
    const actualStageStatus = stageData?.status?.toLowerCase();
    const isActive = actualStageStatus === 'processing' || actualStageStatus === 'in_progress';
    const isFailed = stageStatus.status === 'failed';
    const isLast = index === stages.length - 1;
    const isExpanded = expandedStages.has(stage.key);
    const hasError = stageData?.error;

    return (
      <div key={stage.key} className="relative">
        {/* Timeline Line */}
        {!isLast && (
          <div className={`absolute left-6 top-16 w-0.5 h-20 ${
            isCompleted ? 'bg-gradient-to-b from-brand-green-500 to-brand-emerald-600' : 'bg-gray-300'
          }`}></div>
        )}

        {/* Timeline Item */}
        <div className="flex gap-4">
          {/* Status Circle */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 relative ${
            isCompleted
              ? 'bg-gradient-to-br from-brand-green-100 to-brand-emerald-100 border-brand-green-500 shadow-lg'
              : isActive
                ? 'bg-gradient-to-br from-brand-blue-100 to-brand-cyan-100 border-brand-blue-500 shadow-lg animate-pulse'
                : isFailed
                  ? 'bg-gradient-to-br from-red-100 to-rose-100 border-red-500 shadow-lg'
                  : 'bg-gradient-to-br from-gray-100 to-slate-100 border-gray-300'
          }`}>
            <IconComponent
              className={`w-6 h-6 ${
                isCompleted ? 'text-brand-green-600' :
                isActive ? 'text-brand-blue-600' :
                isFailed ? 'text-red-600' :
                'text-gray-400'
              } ${stageStatus.animate ? 'animate-spin' : ''}`}
            />
          </div>

          {/* Content */}
          <div className="flex-1 pb-8">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
              {/* Header */}
              <div
                className={`p-6 ${isExpanded ? 'border-b border-gray-100 rounded-t-2xl' : 'rounded-2xl'} ${(isActive || isFailed || isCompleted) ? 'cursor-pointer hover:bg-opacity-80' : ''} ${
                  isCompleted ? 'bg-gradient-to-r from-brand-green-50 to-brand-emerald-50' :
                  isActive ? 'bg-gradient-to-r from-brand-blue-50 to-brand-cyan-50' :
                  isFailed ? 'bg-gradient-to-r from-red-50 to-rose-50' :
                  'bg-gradient-to-r from-gray-50 to-slate-50'
                }`}
                onClick={(isActive || isFailed || isCompleted) ? () => toggleStageExpansion(stage.key) : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{stage.label}</h3>
                    <p className="text-sm text-gray-600">{stage.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Show stage elapsed time (real-time for processing, final for completed) */}
                    {stageElapsedTimes[stage.key] && (
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md ${
                        isActive
                          ? 'bg-brand-blue-100 text-brand-blue-700 font-semibold'
                          : isCompleted
                          ? 'bg-brand-green-100 text-brand-green-700 font-medium'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Clock className={`w-3.5 h-3.5 ${isActive ? 'animate-spin' : ''}`} />
                        <span className="text-sm">{Math.round(stageElapsedTimes[stage.key] / 1000)}s</span>
                        {isActive && <span className="animate-pulse text-brand-blue-500">●</span>}
                      </div>
                    )}
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isCompleted ? 'bg-green-100 text-green-800' :
                      isActive ? 'bg-blue-100 text-blue-800' :
                      isFailed ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {isCompleted ? 'Completed' :
                       isActive ? 'In Progress' :
                       isFailed ? 'Failed' :
                       'Pending'}
                    </div>
                    {(isActive || isFailed || isCompleted) && (
                      <div className="text-gray-400 hover:text-gray-600">
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Expandable Content */}
              {isExpanded && (
                <>
                  {/* Progress Bar */}
                  {isActive && (
                    <div className="px-6 py-4 border-t border-gray-100 bg-gradient-to-r from-brand-blue-50 to-brand-cyan-50">
                      <div className="flex items-center justify-between text-sm text-brand-blue-600 mb-2">
                        <span className="font-medium">
                          {stage.key === 'stage_3_questions' && stageData?.progress ? (
                            `Generating questions... ${stageData.progress.generated} / ${stageData.progress.total_questions}`
                          ) : (
                            'Processing...'
                          )}
                        </span>
                      </div>
                      <div className="w-full bg-brand-blue-200 rounded-full h-3 overflow-hidden">
                        {stage.key === 'stage_3_questions' && stageData?.progress ? (
                          <div
                            className="h-3 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-600 rounded-full transition-all duration-500"
                            style={{ width: `${Math.round((stageData.progress.generated / stageData.progress.total_questions) * 100)}%` }}
                          />
                        ) : (
                          <div className="h-3 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-600 rounded-full animate-pulse" style={{ width: '65%' }} />
                        )}
                      </div>
                      {stage.key === 'stage_3_questions' && stageData?.progress && stageData.progress.in_progress > 0 && (
                        <div className="mt-2 text-xs text-brand-blue-600">
                          {stageData.progress.in_progress} questions in progress
                        </div>
                      )}
                    </div>
                  )}

                  {/* Completion Status */}
                  {isCompleted && (
                    <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 rounded-b-2xl">
                      <div className="flex items-center gap-2 text-green-800">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Stage completed successfully</span>
                      </div>
                      {stage.key === 'stage_3_questions' && stageData?.progress && (
                        <div className="mt-3 text-sm text-green-700">
                          Generated {stageData.progress.generated} / {stageData.progress.total_questions} questions
                          {stageData.progress.failed > 0 && ` (${stageData.progress.failed} failed)`}
                        </div>
                      )}
                      {stage.key === 'stage_1_metadata' && stageData?.sample_data && (
                        <div className="mt-3 p-3 bg-white rounded-lg border border-green-200 text-sm">
                          <div className="font-medium text-green-800 mb-1">Generated Metadata:</div>
                          <div className="text-green-700">
                            <span className="font-medium">Name:</span> {stageData.sample_data.certification_name}
                          </div>
                        </div>
                      )}
                      {stage.key === 'stage_2_images' && stageData?.sample_data && (
                        <div className="mt-3 p-4 bg-white rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Image className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-green-800 text-sm">Generated Images</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {stageData.sample_data.banner_url && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-gray-600">Banner Image</p>
                                <img
                                  src={stageData.sample_data.banner_url}
                                  alt="Banner"
                                  className="rounded-lg border-2 border-gray-200 shadow-sm w-full h-auto hover:shadow-md transition-shadow"
                                />
                              </div>
                            )}
                            {stageData.sample_data.thumbnail_url && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-gray-600">Thumbnail Image</p>
                                <img
                                  src={stageData.sample_data.thumbnail_url}
                                  alt="Thumbnail"
                                  className="rounded-lg border-2 border-gray-200 shadow-sm w-full h-auto hover:shadow-md transition-shadow"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error Status */}
                  {isFailed && (
                    <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-red-50 to-rose-50 rounded-b-2xl">
                      <div className="flex items-center gap-2 text-red-800 mb-2">
                        <XCircle className="w-5 h-5" />
                        <span className="font-medium">Stage failed</span>
                      </div>
                      {hasError && (
                        <p className="text-sm text-red-700 mt-2 p-3 bg-white rounded-lg border border-red-200">
                          {stageData.error}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show skeleton loader for initial load
  if (isLoadingStatus && !generationStatus) {
    return (
      <div className="min-h-[calc(100vh-5.25rem)] bg-gradient-to-br from-gray-50 via-brand-purple-50/30 to-brand-blue-50/30">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/admin/certification/create')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Create Certification</span>
          </button>

          {/* Hero Header Skeleton */}
          <div className="bg-gradient-to-br from-brand-purple-600 via-brand-indigo-600 to-brand-blue-600 rounded-3xl shadow-2xl p-8 mb-6 relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <SkeletonLoader type="custom" width="56px" height="56px" className="rounded-xl bg-white/20" />
                <div className="flex-1">
                  <SkeletonLoader type="custom" width="300px" height="32px" className="mb-2 bg-white/20" />
                  <SkeletonLoader type="custom" width="250px" height="16px" className="bg-white/10" />
                </div>
              </div>
              <SkeletonLoader type="custom" width="100%" height="20px" className="bg-white/10" />
            </div>
          </div>

          {/* Metrics Skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
                <div className="flex items-center gap-3">
                  <SkeletonLoader type="custom" width="48px" height="48px" className="rounded-lg" />
                  <div className="flex-1">
                    <SkeletonLoader type="text" className="h-3 w-20 mb-2" />
                    <SkeletonLoader type="text" className="h-6 w-12" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stages Skeletons */}
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <CardSkeleton key={i} showImage={false} lines={2} className="border border-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-5.25rem)] bg-gradient-to-br from-gray-50 via-brand-purple-50/30 to-brand-blue-50/30">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/certification/create')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Create Certification</span>
          </button>

          {/* Hero Header */}
          <div className="bg-gradient-to-br from-brand-purple-600 via-brand-indigo-600 to-brand-blue-600 rounded-3xl shadow-2xl p-8 mb-6 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-purple-600/20 via-transparent to-brand-blue-600/20"></div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold">AI Certification Generation</h1>
                      <p className="text-brand-purple-100 text-sm mt-1">
                        {generationStatus?.stages?.stage_1_metadata?.sample_data?.certification_name || 'Generating your professional certification...'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`px-4 py-2 rounded-full backdrop-blur-sm border-2 font-semibold text-sm ${
                  generationStatus?.overall_status?.toLowerCase() === 'completed'
                    ? 'bg-green-500/20 border-green-300 text-white'
                    : isGenerating
                    ? 'bg-yellow-500/20 border-yellow-300 text-white animate-pulse'
                    : 'bg-red-500/20 border-red-300 text-white'
                }`}>
                  {generationStatus?.overall_status?.toLowerCase() === 'completed' ? '✓ Completed' : isGenerating ? '⚡ Generating' : '✗ Failed'}
                </div>
              </div>

              {/* Status Message */}
              <p className="text-brand-purple-100 mb-4">
                {isGenerating ? (
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4 animate-pulse" />
                    AI is crafting your certification through 3 intelligent stages...
                  </span>
                ) : generationStatus?.overall_status?.toLowerCase() === 'completed' ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Your certification has been successfully generated and is ready to use!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Generation encountered an issue. You can retry below.
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Metrics Dashboard */}
          {generationStatus && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-purple-100 to-brand-indigo-100 rounded-lg">
                    <BookOpen className="w-5 h-5 text-brand-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Questions</p>
                    <p className="text-xl font-bold text-gray-900">
                      {generationStatus.stages?.stage_3_questions?.progress?.total_questions || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-green-100 to-brand-emerald-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-brand-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Generated</p>
                    <p className="text-xl font-bold text-gray-900">
                      {generationStatus.stages?.stage_3_questions?.progress?.generated || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-blue-100 to-brand-cyan-100 rounded-lg">
                    <Clock className="w-5 h-5 text-brand-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">
                      {isGenerating ? 'Elapsed Time' : 'Total Duration'}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {isGenerating
                        ? `${Math.floor(elapsedTime / 1000)}s`
                        : generationStatus.metrics?.total_duration_ms
                        ? `${Math.round(generationStatus.metrics.total_duration_ms / 1000)}s`
                        : '0s'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-pink-100 to-rose-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-brand-pink-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Progress</p>
                    <p className="text-xl font-bold text-gray-900">
                      {Math.round(([
                        generationStatus.stages?.stage_1_metadata?.status,
                        generationStatus.stages?.stage_2_images?.status,
                        generationStatus.stages?.stage_3_questions?.status
                      ].filter(s => s?.toLowerCase() === 'completed').length / 3) * 100)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Metadata Preview Card */}
          {generationStatus?.stages?.stage_1_metadata?.sample_data && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-5 h-5 text-brand-purple-600" />
                <h2 className="text-lg font-bold text-gray-900">Certification Details</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-brand-purple-500" />
                    <span className="text-sm font-semibold text-gray-700">Name</span>
                  </div>
                  <p className="text-gray-900 font-medium pl-6">
                    {generationStatus.stages.stage_1_metadata.sample_data.certification_name}
                  </p>
                </div>

                {generationStatus.stages.stage_1_metadata.sample_data.description_preview && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-brand-blue-500" />
                      <span className="text-sm font-semibold text-gray-700">Description</span>
                    </div>
                    <p className="text-gray-600 pl-6 text-sm leading-relaxed">
                      {generationStatus.stages.stage_1_metadata.sample_data.description_preview}
                    </p>
                  </div>
                )}

                {generationStatus.stages.stage_1_metadata.sample_data.targeted_audience && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-brand-green-500" />
                      <span className="text-sm font-semibold text-gray-700">Target Audience</span>
                    </div>
                    <p className="text-gray-600 pl-6 text-sm">
                      {generationStatus.stages.stage_1_metadata.sample_data.targeted_audience}
                    </p>
                  </div>
                )}

                {generationStatus.stages.stage_1_metadata.sample_data.tags && generationStatus.stages.stage_1_metadata.sample_data.tags.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-brand-pink-500" />
                      <span className="text-sm font-semibold text-gray-700">Tags</span>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-6">
                      {generationStatus.stages.stage_1_metadata.sample_data.tags.map((tag: string, index: number) => (
                        <span key={index} className="px-3 py-1 bg-gradient-to-r from-brand-purple-100 to-brand-indigo-100 text-brand-purple-700 text-xs font-medium rounded-full border border-brand-purple-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Generation Stages */}
        <div className="space-y-4">
          {stages.map((stage, index) => renderTimelineItem(stage, index))}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex items-center justify-center gap-4">
          {generationStatus?.overall_status?.toLowerCase() === 'completed' ? (
            <div className="flex flex-col items-center gap-4">
              {/* Success Message */}
              <div className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl shadow-lg">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-bold text-green-900 text-lg">All Stages Completed Successfully!</p>
                  <p className="text-sm text-green-700">Your certification is ready to be published and assigned to learners.</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => navigate(`/admin/certifications/${id}`)}
                  disabled={!canViewDetails}
                  title={!canViewDetails ? 'You do not have permission' : undefined}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 text-white rounded-xl hover:from-brand-purple-700 hover:to-brand-indigo-700 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Eye className="w-5 h-5" />
                  View Certification Details
                </button>

                <button
                  onClick={() => navigate('/admin/certifications')}
                  disabled={!canGoToCertifications}
                  title={!canGoToCertifications ? 'You do not have permission' : undefined}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Award className="w-5 h-5" />
                  Go to Certifications
                </button>
              </div>
            </div>
          ) : !isGenerating && generationStatus && (
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRetrying ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Retrying Generation...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Retry Generation
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
